<template>
  <div class="sidebar-wrapper">
    <div class="sidebar">
      <div>
        <div class="sidebar-topbar">
          <div :class="['sidebar-header', SHOW_UNSAVED_CHANGES_CSS_CLASS_NAME]">
            <a @click="navigateToManagerPortal">
              <img src="/logo.svg" alt="application logo" class="logo-icon" />
              <span class="logo-text">Conversation<br/>Analytics</span>
            </a>
          </div>
          <sl-tooltip placement="right" :content="pinSidebarButtonTooltipText" hoist> 
            <span :class="['icon-sidebar', 'pin-sidebar-btn', { 'active': isSidebarExpanded }] " @click="userProfileStore.toggleSidebarState()"></span>
          </sl-tooltip>
        </div>
        <nav>
          <ul class="nav-menu">
            <NuxtLink
              v-for="item in navMenuItems"
              :key="item.label"
              :to="item.value"
              :title="item.label"
              class="nav-menu-item"
              activeClass="active-nav-item"
            >
              <span :class="[isMenuItemActive(item.value) ? item.activeIcon : item.icon, 'nav-menu-item-icon']"></span>
              <span class="nav-menu-item-text">{{ item.label }}</span>
            </NuxtLink>
          </ul>
        </nav>
      </div>

      <filter-view ref="filterView" :class="['filter-view', {'active-nav-item': filterView?.isViewModeActive }]"/>
      <div class="bottom-menu">
        <the-beamer/>
        <the-userflow/>
        <the-user-menu class="user-menu"/>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter } from "nuxt/app";
import "@shoelace-style/shoelace/dist/components/tooltip/tooltip.js";
import FilterView from "@/components/shared/filters/FilterView/FilterView.vue";
import TheUserMenu from "@/components/shared/layout/TheUserMenu.vue";
import TheBeamer from "@/components/shared/layout/TheBeamer.vue";
import TheUserflow from "@/components/shared/layout/TheUserflow.vue";
import { useUserProfileStore } from "@/store/userProfileStore";
import { SHOW_UNSAVED_CHANGES_CSS_CLASS_NAME } from "@/constants/cssClassNames";
import { INavMenuItem } from "@/models/menu.model";

defineProps<{
  navMenuItems: INavMenuItem[];
}>();

const userProfileStore = useUserProfileStore();
const router = useRouter();

const filterView = ref();

const isMenuItemActive = (path: string) => router.currentRoute.value.path === path;
const isSidebarExpanded = computed(() => userProfileStore.isSidebarExpanded);
const pinSidebarButtonTooltipText = computed(() => `${isSidebarExpanded.value ? 'Collapse' : 'Expand'} side panel`);

const navigateToManagerPortal = (): void => {
  const portalLink = userProfileStore.portalLink ?? "/";
  window.location.href = portalLink;
};
</script>

<style>
:root {
  --sidebar-nav-menu-item-height: 40px;
  --sidebar-nav-menu-item-left-border-width: 5px;
  --sidebar-active-nav-menu-item-icon-color: var(--base-bright-pink);
}
</style>

<style scoped lang="scss">
.sidebar-wrapper {
  container: sidebar-wrapper / size;
  display: flex;
  flex-direction: column;
  padding: 0;
  height: 100%;
  background-color: var(--base-dark-purple);

  @container sidebar-wrapper (width > 200px) {
    .sidebar-header {
      .logo-text {
        display: flex;
      }
    }

    .pin-sidebar-btn {
      display: flex;
    }

    .nav-menu {
      .nav-menu-item {
        justify-content: flex-start;

        .nav-menu-item-text {
          display: block;
        }
      }
    }

    .filter-view {
      --expandable-section-title-icon-padding: 0 14px 0 8px;
      
      height: 100%;
      padding: 0 6px 8px;
      
      :deep(.expandable-content) {
        display: block;
      }

      :deep(.state-icon-wrapper) {
        display: flex;
      }

      :deep(.title) {
        display: block;
      }

      &.active-nav-item {
        background: transparent;

        &::before {
          display: none;
        }
      }
    }

    .user-menu {
      padding: 0 4px;

      :deep(.user-menu-button) {
        justify-content: flex-start;

        .user-menu-button-label {
          display: block;
        }
      }

      :deep(.dropdown-content) {
        margin-left: 20px;
      }
    }

    :deep(.nav-menu-plugin-button) {
      --button-justify-content: flex-start;

      .nav-menu-plugin-button-text {
        display: block;
      }
    }
  }
}

.sidebar {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 8px 6px;
  height: 100%;
}

.sidebar-topbar {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0 4px;
  height: 34px;
}

.sidebar-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin: 0;
  cursor: pointer;

  a {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 10px;
  }

  .logo-icon {
    height: 19px;
    width: 30px;
  }

  .logo-text {
    display: none;
    font-family: var(--font-family);
    font-size: var(--font-size-headers-xx-small);
    font-weight: var(--font-weight-body-medium);
    color: var(--colour-text-neutral-default-dark);
    line-height: var(--line-height-xx-s);
  }
}

.pin-sidebar-btn {
  display: none;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-body-small);
  height: 24px;
  width: 24px;
  color: var(--colour-icons-neutral-default-dark);
  border-radius: 3px;
  cursor: pointer;

  &:hover {
    background-color: var(--colour-background-neutral-hover-a-dark);
  }

  &.active {
    background: var(--colour-background-neutral-default-a-dark);
  }
}

.nav-menu {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2px;
  margin: 20px 0 0;
  padding: 0;

  .nav-menu-item {
    width: 100%;
    height: var(--sidebar-nav-menu-item-height);
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    font-size: var(--font-size-headers-x-small);
    font-weight: var(--font-weight-heading-regular);
    color: var(--colour-text-neutral-default-dark);
    text-decoration: none;
    border-radius: 3px;
    padding: 8px;
    gap: 8px;

    .nav-menu-item-text {
      display: none;
    }

    .nav-menu-item-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      font-size: 14px;
      color: var(--colour-icons-contrast);
    }

    &:hover {
      background: var(--colour-background-neutral-hover-a-dark);
    }

    &.active-nav-item {
      .nav-menu-item-icon {
        color: var(--sidebar-active-nav-menu-item-icon-color);
        font-weight: var(--font-weight-heading);
      }
    }
  }
}

.active-nav-item {
  font-weight: var(--font-weight-body-medium);
  color: var(--colour-text-brand-default-dark);
  background: var(--colour-background-neutral-default-a-dark);

  &::before {
    content: "";
    position: absolute;
    top: var(--sidebar-nav-menu-item-left-border-width);
    left: 0;
    width: var(--sidebar-nav-menu-item-left-border-width);
    height: calc(100% - 2 * var(--sidebar-nav-menu-item-left-border-width));
    background-color: var(--base-bright-pink);
    border-radius: 0px 4px 4px 0px;
  }
}

.filter-view {
  --expandable-section-title-icon-padding: 0 14px;

  height: var(--sidebar-nav-menu-item-height);
  position: relative;
  padding-bottom: 0;

  :deep(.expandable-content) {
    display: none;
  }

  :deep(.state-icon-wrapper) {
    display: none;
  }

  :deep(.title) {
    display: none;
  }

  &.active-nav-item {
    --expandable-section-title-icon-color: var(--sidebar-active-nav-menu-item-icon-color);
  }
}
  
.bottom-menu {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: auto;

  :deep(.user-menu-button) {
    justify-content: center;

    .user-menu-button-label {
      display: none;
    }
  }
  
  :deep(.dropdown-content) {
    margin-left: 15px;
  }
}

:deep(.nav-menu-plugin-button) {
  --button-justify-content: center;

  .nav-menu-plugin-button-text {
    display: none;
  }
}
</style>
